import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { CheckCircle, XCircle } from "lucide-react";
import { AIDealAnalysis } from "./AIDealAnalysis";

interface DealReviewDialogProps {
  deal: any;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAction: (dealId: string, action: "approve" | "reject", reason?: string) => void;
}

export const DealReviewDialog = ({ deal, open, onOpenChange, onAction }: DealReviewDialogProps) => {
  const [rejectionReason, setRejectionReason] = useState("");
  const [action, setAction] = useState<"approve" | "reject" | null>(null);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
    }).format(amount);
  };

  const handleSubmit = () => {
    if (action === "reject" && !rejectionReason.trim()) {
      return;
    }
    onAction(deal.id, action!, action === "reject" ? rejectionReason : undefined);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{deal.title}</DialogTitle>
          <DialogDescription>
            Review deal details and make compliance decision
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* AI Analysis */}
          <AIDealAnalysis deal={{
            title: deal.title,
            description: deal.description,
            brand_name: deal.brand_name,
            amount: deal.amount,
            deliverables: deal.deliverables,
            deal_type: deal.deal_type
          }} />

          <Separator />

          {/* Deal Overview */}
          <div className="space-y-3">
            <h3 className="font-semibold">Deal Overview</h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-muted-foreground">Athlete</p>
                <p className="font-medium">{deal.athletes?.name}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Sport</p>
                <p className="font-medium">{deal.athletes?.sport}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Brand</p>
                <p className="font-medium">{deal.brand_name}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Deal Type</p>
                <Badge variant="outline">{deal.deal_type}</Badge>
              </div>
              <div>
                <p className="text-muted-foreground">Amount</p>
                <p className="font-bold text-lg">{formatCurrency(deal.amount)}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Duration</p>
                <p className="font-medium">
                  {new Date(deal.start_date).toLocaleDateString()} -{" "}
                  {deal.end_date ? new Date(deal.end_date).toLocaleDateString() : "Ongoing"}
                </p>
              </div>
            </div>
          </div>

          <Separator />

          {/* Description */}
          {deal.description && (
            <>
              <div>
                <h3 className="font-semibold mb-2">Description</h3>
                <p className="text-sm text-muted-foreground">{deal.description}</p>
              </div>
              <Separator />
            </>
          )}

          {/* Brand Contact */}
          {(deal.brand_contact_email || deal.brand_contact_phone) && (
            <>
              <div>
                <h3 className="font-semibold mb-2">Brand Contact Information</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  {deal.brand_contact_email && (
                    <div>
                      <p className="text-muted-foreground">Email</p>
                      <p className="font-medium">{deal.brand_contact_email}</p>
                    </div>
                  )}
                  {deal.brand_contact_phone && (
                    <div>
                      <p className="text-muted-foreground">Phone</p>
                      <p className="font-medium">{deal.brand_contact_phone}</p>
                    </div>
                  )}
                </div>
              </div>
              <Separator />
            </>
          )}

          {/* Action Selection */}
          {!action && (
            <div className="flex gap-3">
              <Button
                variant="default"
                className="flex-1"
                onClick={() => setAction("approve")}
              >
                <CheckCircle className="mr-2 h-4 w-4" />
                Approve Deal
              </Button>
              <Button
                variant="destructive"
                className="flex-1"
                onClick={() => setAction("reject")}
              >
                <XCircle className="mr-2 h-4 w-4" />
                Reject Deal
              </Button>
            </div>
          )}

          {/* Rejection Reason */}
          {action === "reject" && (
            <div className="space-y-2">
              <Label htmlFor="rejection_reason">Rejection Reason *</Label>
              <Textarea
                id="rejection_reason"
                placeholder="Please provide a detailed reason for rejection..."
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                rows={4}
              />
            </div>
          )}

          {/* Approval Confirmation */}
          {action === "approve" && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <p className="text-sm text-green-800">
                ✓ This deal will be marked as <strong>Active</strong> and the athlete will be notified.
              </p>
            </div>
          )}
        </div>

        {action && (
          <DialogFooter>
            <Button variant="outline" onClick={() => setAction(null)}>
              Back
            </Button>
            <Button
              onClick={handleSubmit}
              variant={action === "approve" ? "default" : "destructive"}
              disabled={action === "reject" && !rejectionReason.trim()}
            >
              {action === "approve" ? "Confirm Approval" : "Confirm Rejection"}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
};